<template>
  <div class="h-full max-h-screen w-full bg-background">
    <page-header
      backTo="tools-ivy-clients"
      title="iV Client Detail"
      :enableLogicalBack="false"
    >
      <button
        v-if="client && !client.is_archived"
        @click="archiveClient"
        class="border hover:shadow transition flex items-center gap-2 rounded-md border-orange-400 bg-orange-200 px-2 py-1 text-secondary duration-300 hover:border-orange-500 hover:bg-orange-300 active:shadow-inner mr-2"
      >
        <svg
          class="h-4 w-4 stroke-current"
          fill="none"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          viewBox="0 0 24 24"
        >
          <path d="M21 8v13H3V8M1 3h22l-2 5H3L1 3zM10 12h4" />
        </svg>
        <span>Archive</span>
      </button>
      <button
        v-if="client && client.is_archived"
        @click="unarchiveClient"
        class="border hover:shadow transition flex items-center gap-2 rounded-md border-blue-400 bg-blue-200 px-2 py-1 text-secondary duration-300 hover:border-blue-500 hover:bg-blue-300 active:shadow-inner mr-2"
      >
        <svg
          class="h-4 w-4 stroke-current"
          fill="none"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          viewBox="0 0 24 24"
        >
          <path d="M21 8v13H3V8M1 3h22l-2 5H3L1 3zM14 12l-4-4M10 8v8" />
        </svg>
        <span>Unarchive</span>
      </button>
      <button
        v-if="client && !client.is_revoked"
        @click="revokeClient"
        class="border hover:shadow transition flex items-center gap-2 rounded-md border-red-400 bg-red-200 px-2 py-1 text-secondary duration-300 hover:border-red-500 hover:bg-red-300 active:shadow-inner mr-2"
      >
        <svg
          class="h-4 w-4 stroke-current"
          fill="none"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          viewBox="0 0 24 24"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M4.93 4.93l14.14 14.14" />
        </svg>
        <span>Revoke</span>
      </button>
      <button
        v-if="client && client.is_revoked"
        @click="unrevokeClient"
        class="border hover:shadow transition flex items-center gap-2 rounded-md border-purple-400 bg-purple-200 px-2 py-1 text-secondary duration-300 hover:border-purple-500 hover:bg-purple-300 active:shadow-inner mr-2"
      >
        <svg
          class="h-4 w-4 stroke-current"
          fill="none"
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          viewBox="0 0 24 24"
        >
          <path d="M9 12l2 2 4-4M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9c1.66 0 3.22.45 4.55 1.24" />
        </svg>
        <span>Unrevoke</span>
      </button>
      <button
        @click="showSendMessagePanel = true"
        class="border hover:shadow transition flex items-center gap-2 rounded-md border-green-400 bg-green-200 px-2 py-1 text-secondary duration-300 hover:border-green-500 hover:bg-green-300 active:shadow-inner"
      >
        <span class="pi pi-send"></span>
        <span>Send Message</span>
      </button>
    </page-header>

    <main class="p-6 pb-20 bg-background flex-1 overflow-y-scroll h-full">
      <div
        v-if="!client"
        class="rounded flex items-center justify-center bg-white py-20 shadow-md"
      >
        <spinner :color="'text-black'" :size="10" />
      </div>

      <div v-else>
        <!-- Status Banners -->
        <div v-if="client.is_revoked" class="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded shadow-md mb-4">
          <div class="flex items-center">
            <svg
              class="w-5 h-5 mr-2 stroke-current"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              viewBox="0 0 24 24"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M4.93 4.93l14.14 14.14" />
            </svg>
            <span class="font-semibold">This client has been revoked</span>
          </div>
        </div>
        
        <div v-if="client.is_archived" class="bg-orange-100 border-l-4 border-orange-500 text-orange-700 p-4 rounded shadow-md mb-4">
          <div class="flex items-center">
            <svg
              class="w-5 h-5 mr-2 stroke-current"
              fill="none"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              viewBox="0 0 24 24"
            >
              <path d="M21 8v13H3V8M1 3h22l-2 5H3L1 3zM10 12h4" />
            </svg>
            <span class="font-semibold">This client has been archived</span>
          </div>
        </div>

        <div class="flex w-full space-x-4">
          <div class="rounded flex w-full flex-1 bg-white p-5 shadow-md">
            <div class="w-2/3">
              <h2 class="mb-4 text-3xl">
                {{ client.client_name }}
                <button
                  @click="openEditPanel"
                  v-if="
                    client.metadata && client.metadata.activated_product_codes
                  "
                  class="rounded border transition ml-4 border-transparent bg-white p-1 text-secondary duration-100 hover:border-secondary active:border-secondary active:bg-gray-200 active:shadow-inner"
                >
                  <svg
                    class="h-6 w-6 stroke-current"
                    fill="none"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M17 3a2.828 2.828 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z"
                    />
                  </svg>
                </button>
              </h2>

              <div v-if="client.org_name" class="flex items-center py-1">
                <div>
                  <svg
                    class="mr-2 h-5 w-5 stroke-current text-orange-700"
                    fill="none"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    viewBox="0 0 24 24"
                  >
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </div>
                <p class="rounded-lg px-2 py-1">
                  Organisation: {{ client.org_name }}
                </p>
              </div>

              <div v-if="client.agent_name" class="flex items-center py-1">
                <div>
                  <svg
                    class="mr-2 h-5 w-5 stroke-current text-orange-700"
                    fill="none"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    viewBox="0 0 24 24"
                  >
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </div>
                <p class="rounded-lg px-2 py-1">
                  Agent: {{ client.agent_name }}
                </p>
              </div>

              <div class="flex items-center py-1">
                <div>
                  <svg
                    class="mr-2 h-5 w-5 stroke-current text-orange-700"
                    fill="none"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    viewBox="0 0 24 24"
                  >
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </div>
                <p class="rounded-lg px-2 py-1">
                  {{ formatDefaultAddress() }}
                </p>
              </div>

              <div class="flex items-center py-1">
                <div>
                  <svg
                    class="mr-2 h-5 w-5 stroke-current text-orange-700"
                    fill="none"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    viewBox="0 0 24 24"
                  >
                    <rect
                      x="1"
                      y="4"
                      width="22"
                      height="16"
                      rx="2"
                      ry="2"
                    ></rect>
                    <line x1="1" y1="10" x2="23" y2="10"></line>
                  </svg>
                </div>
                <p class="rounded-lg px-2 py-1">
                  Payment Status:
                  {{ client.metadata && client.metadata.payment_status || "Pending" }}
                </p>
              </div>

              <div class="flex items-center py-1 gap-2">
                <span class="pi pi-code text-orange-700 w-5 h-5"></span>
                <p class="rounded-lg px-2 py-1">
                  Brand:
                  {{ formatBrandName() }}
                </p>
              </div>

              <div class="flex items-center py-1 gap-2">
                <span class="pi pi-code text-orange-700 w-5 h-5"></span>
                <p class="rounded-lg px-2 py-1">
                  Actication Code:
                  {{ client.activation_code || client.metadata && client.metadata.activation_code_was }}
                </p>
              </div>

              <div class="flex items-center py-1 gap-2">
                <span class="pi pi-code text-orange-700 w-5 h-5"></span>
                <p class="rounded-lg px-2 py-1">
                  VTS Number:
                  {{ client.vts_number || '---' }}
                </p>
              </div>

              <div v-if="client.is_activated">
                <div class="flex items-center py-1 gap-2"> 
                  <span class="pi pi-code text-orange-700 w-5 h-5"></span>
                  <p class="rounded-lg px-2 py-1">
                    Activated On:
                    {{ 
                      $moment
                        .unix(client.activated_at)
                        .format("DD/MM/YYYY [at] HH:mm") 
                    }}
                  </p>
                </div>

                <div class="flex items-center py-1 gap-2"> 
                  <span class="pi pi-code text-orange-700 w-5 h-5"></span>
                  <p class="rounded-lg px-2 py-1">
                    IoT Device ID:
                    {{ client.iot_device_id || '---' }}
                  </p>
                </div>
              </div>


              <div
                v-if="client.metadata && client.metadata.created_by_boston_user"
                class="flex items-center py-1"
              >
                <div>
                  <svg
                    class="mr-2 h-5 w-5 stroke-current text-orange-700"
                    fill="none"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    viewBox="0 0 24 24"
                  >
                    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </div>
                <p class="rounded-lg px-2 py-1">
                  Created by: {{ client.metadata && client.metadata.created_by_boston_user }}
                </p>
              </div>

              <div v-if="client.metadata && client.metadata.notes" class="flex items-center py-1">
                <div>
                  <svg
                    class="mr-2 h-5 w-5 stroke-current text-orange-700"
                    fill="none"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="M17 3a2.828 2.828 0 114 4L7.5 20.5 2 22l1.5-5.5L17 3z"
                    />
                  </svg>
                </div>
                <p class="rounded-lg px-2 py-1">
                  Notes: {{ client.metadata && client.metadata.notes }}
                </p>
              </div>
            </div>

            <!-- Is Customer/Agent/Supplier flags -->
            <div class="items-top mt-2 flex w-1/3 justify-end">
              <div class="flex flex-col items-center align-top">
                <div class="-mx-1 flex flex-wrap">
                  <div
                    class="mx-1 mb-1 flex select-none items-center rounded-lg border-2 border-gray-600 px-2 py-1 text-sm font-bold uppercase leading-none text-gray-700"
                    v-if="!client.is_activated"
                  >
                    <span>Not Activated</span>
                  </div>
                  <div
                    class="mx-1 mb-1 flex select-none items-center rounded-lg border-2 border-green-600 px-2 py-1 text-sm font-bold uppercase leading-none text-green-700"
                    v-if="client.is_activated"
                  >
                    <span>Activated</span>
                  </div>
                  <div
                    class="mx-1 mb-1 flex select-none items-center rounded-lg border-2 border-orange-600 px-2 py-1 text-sm font-bold uppercase leading-none text-orange-700"
                    v-if="client.is_archived"
                  >
                    <span>Archived</span>
                  </div>
                  <div
                    class="mx-1 mb-1 flex select-none items-center rounded-lg border-2 border-red-600 px-2 py-1 text-sm font-bold uppercase leading-none text-red-700"
                    v-if="client.is_revoked"
                  >
                    <span>Revoked</span>
                  </div>
                </div>
              </div>
            </div>
            <!-- Is Customer/Agent/Supplier flags -->
          </div>
        </div>

        <div class="rounded gap mt-6 w-full bg-white p-5 shadow-md">
          <div
            v-if="error"
            class="border mb-4 rounded-lg border-red-500 bg-red-200 px-5 py-4 text-red-900"
          >
            <p>Couldn't load client: {{ error.statusText }}</p>
          </div>
          <spinner
            v-if="viewState === 'Loading'"
            :size="6"
            color="text-black"
            class="mx-auto"
          />
          <IvyClientDetailPanel v-else :client="client" />
        </div>
      </div>
    </main>

    <portal to="overlay-outlet">
      <panel
        :showing="editDetailsPanelOpen"
        @close="editDetailsPanelOpen = false"
        title="Edit Client Details"
      >
        <EditClientDetails :client="client" @complete="closeEditDetailsPanel" />
      </panel>
    </portal>

    <portal to="overlay-outlet">
      <panel
        :showing="showSendMessagePanel"
        @close="showSendMessagePanel = false"
        title="Send Client Message"
      >
        <SendClientMessage
          :client="client"
          @message-sent="showSendMessagePanel = false"
        />
      </panel>
    </portal>
  </div>
</template>

<script>
import PageHeader from "@/components/PageHeader.vue";
import Spinner from "@/components/Spinner.vue";
import Panel from "@/components/Panel.vue";
import EditClientDetails from "@/components/Tools/IvyClients/EditClientDetails.vue";
import SendClientMessage from "@/components/Tools/IvyClients/SendClientMessage.vue";
import IvyClientDetailPanel from "@/views/Tools/IvyClients/IvyClientDetailPanel.vue";
import notificationMixin from "@/mixins/notificationMixin";

export default {
  name: "IvyClientDetail",
  mixins: [notificationMixin],
  components: {
    IvyClientDetailPanel,
    SendClientMessage,
    PageHeader,
    Spinner,
    Panel,
    EditClientDetails,
  },
  props: {
    clientId: String,
  },
  data() {
    return {
      viewState: "Idle",
      error: null,
      client: null,
      brandMap: null,
      editDetailsPanelOpen: false,
      showSendMessagePanel: false,
    };
  },
  async mounted() {
    await this.loadClientDetails();
  },
  methods: {
    async loadClientDetails() {
      this.viewState = "Loading";
      this.client = await this.iVClientService.getIvyActivationById(this.clientId);
      this.brandMap = await this.iVClientService.getIvyActivationParameters();

      this.viewState = "Loaded";
    },
    formatDefaultAddress: function () {
      return [
        this.client.location_name,
        this.client.location_address.address1,
        this.client.location_address.address2,
        this.client.location_address.address3,
        this.client.location_address.city,
        this.client.location_address.zip,
      ]
        .filter((part) => !!part)
        .join(", ");
    },
    formatBrandName: function () {
      const brand = this.brandMap.brand_ids.find((b) => b.id === this.client.brand_id);
      return brand ? brand.name : "---";
    },
    openEditPanel: function () {
      this.editDetailsPanelOpen = true;
    },
    closeEditDetailsPanel: async function () {
      this.editDetailsPanelOpen = false;
      await this.loadClientDetails();
    },
    archiveClient: async function () {
      const confirmed = await this.confirmAction({
        message: `Are you sure you want to archive "${this.client.client_name}"?`,
        header: "Archive Client",
        acceptLabel: "Archive",
        rejectLabel: "Cancel",
        icon: "pi pi-archive",
        acceptClass: "p-button-warning",
      });

      if (!confirmed) return;

      try {
        this.viewState = "Loading";
        await this.iVClientService.archiveClient(this.clientId);

        this.client.is_archived = true;
        this.notifySuccess(`"${this.client.client_name}" has been archived`);
        this.viewState = "Loaded";
      } 
      catch (error) {
        this.notifyError("Failed to archive client");
        console.error("Error archiving client:", error);
        this.viewState = "Loaded";
      }
    },
    unarchiveClient: async function () {
      const confirmed = await this.confirmAction({
        message: `Are you sure you want to unarchive "${this.client.client_name}"?`,
        header: "Unarchive Client",
        acceptLabel: "Unarchive",
        rejectLabel: "Cancel",
        icon: "pi pi-replay",
        acceptClass: "p-button-info",
      });

      if (!confirmed) return;

      try {
        this.viewState = "Loading";
        await this.iVClientService.unarchiveClient(this.clientId);

        this.client.is_archived = false;
        this.notifySuccess(`"${this.client.client_name}" has been unarchived`);
        this.viewState = "Loaded";
      } 
      catch (error) {
        this.notifyError("Failed to unarchive client");
        console.error("Error unarchiving client:", error);
        this.viewState = "Loaded";
      }
    },
    revokeClient: async function () {
      const confirmed = await this.confirmAction({
        message: `Are you sure you want to revoke "${this.client.client_name}"?`,
        header: "Revoke Client",
        acceptLabel: "Revoke",
        rejectLabel: "Cancel",
        icon: "pi pi-ban",
        acceptClass: "p-button-danger",
      });

      if (!confirmed) return;

      try {
        this.viewState = "Loading";
        await this.iVClientService.revokeClient(this.clientId);
 
        this.client.is_revoked = true;
        this.notifySuccess(`"${this.client.client_name}" has been revoked`);
        this.viewState = "Loaded";
      } 
      catch (error) {
        this.notifyError("Failed to revoke client");
        console.error("Error revoking client:", error);
        this.viewState = "Loaded";
      }
    },
    unrevokeClient: async function () {
      const confirmed = await this.confirmAction({
        message: `Are you sure you want to unrevoke "${this.client.client_name}"?`,
        header: "Unrevoke Client",
        acceptLabel: "Unrevoke",
        rejectLabel: "Cancel",
        icon: "pi pi-check-circle",
        acceptClass: "p-button-success",
      });

      if (!confirmed) return;

      try {
        this.viewState = "Loading";
        await this.iVClientService.unrevokeClient(this.clientId);
 
        this.client.is_revoked = false;
        this.notifySuccess(`"${this.client.client_name}" has been unrevoked`);
        this.viewState = "Loaded";
      } 
      catch (error) {
        this.notifyError("Failed to unrevoke client");
        console.error("Error unrevoking client:", error);
        this.viewState = "Loaded";
      }
    },
  },
};
</script>
